import { useState } from 'react';
import { FormikProps } from 'formik';
import { useTranslation } from '@shared/core';
import { useFilestack } from '@shared/utils/filestack';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useUploadPhotoFromUrlMutation } from '@graphql/generated';

interface HotelPhotoProps<T extends { customPhotoId?: string | null }> {
  containerId: string;
  formik: FormikProps<T>;
  hotelPhoto?: string;
  defaultPhoto?: string;
  onSaveError: (message: string) => void;
}

export function useHotelPhoto<T extends { customPhotoId?: string | null }>(props: HotelPhotoProps<T>) {
  const { containerId, formik, hotelPhoto: hotelPhotoProps, defaultPhoto: defaultPhotoProps, onSaveError } = props;
  const { tq } = useTranslation('websiteEditor');

  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [hotelPhoto, setHotelPhoto] = useState(hotelPhotoProps);
  const [defaultPhoto, setDefaultPhoto] = useState(defaultPhotoProps);

  const [uploadPhotoFromUrl, { loading: waitingUploadPhoto }] = useUploadPhotoFromUrlMutation();

  const { open: openFilePicker, getPhotoPreviewUrl } = useFilestack({
    containerId,
    accept: 'image/*',
    onUploadDone: async (res): Promise<void> => {
      if (res.filesFailed.length) {
        onSaveError(tq`An error occurred while saving your photo. Please try again.`);
        openFilePicker();
        return;
      }
      const { url, filename } = res.filesUploaded[0];
      const previewUrl = await getPhotoPreviewUrl({ url });
      setHotelPhoto(previewUrl);
      setIsUploadingPhoto(false);

      try {
        const { data } = await uploadPhotoFromUrl({
          variables: {
            payload: {
              assetId: filename,
              containerId,
              url: previewUrl,
            },
          },
        });
        const customPhotoId = data?.uploadPhotoFromUrl?.id;
        if (customPhotoId) {
          formik.setFieldValue('customPhotoId', customPhotoId);
        } else {
          onSaveError(tq`An error occurred while saving your photo. Please try again.`);
        }
      } catch {
        onSaveError(tq`An error occurred while saving your photo. Please try again.`);
      }
    },
    onClose: () => {
      setIsUploadingPhoto(false);
    },
    maxFiles: 1,
  });

  const onUpdateHotelPhoto = useEventCallback(async () => {
    setIsUploadingPhoto(true);
    openFilePicker();
  });

  const onDeleteHotelPhoto = useEventCallback(() => {
    formik.setFieldValue('customPhotoId', null);
    formik.setFieldValue('photoUrl', null);
    setHotelPhoto('');
  });

  const onResetHotelPhoto = useEventCallback(() => {
    formik.setFieldValue('customPhotoId', null);
    formik.setFieldValue('photoUrl', defaultPhoto);
    setHotelPhoto(defaultPhoto);
  });

  const setAutoGeneratedPhoto = useEventCallback((photo: string) => {
    setHotelPhoto(photo);
    setDefaultPhoto(photo);
  });

  return {
    hotelPhoto,
    hasDefaultPhoto: !!defaultPhoto && defaultPhoto !== hotelPhoto,
    isUploadingPhoto: isUploadingPhoto || waitingUploadPhoto,
    setAutoGeneratedPhoto,
    onUpdateHotelPhoto,
    onDeleteHotelPhoto,
    onResetHotelPhoto,
    setHotelPhoto,
  };
}
